import React, { useState, useEffect, useRef } from "react";
import { MdClose, MdSearch } from "react-icons/md";
import { list, updateProduct } from "../auth/apiProduct";
import { getProductPrimaryImage } from "../auth/elakay-utils";
import { toImageUrl } from "../../config";
import { elakayAutoText } from "./dataElakay";
import imgProductLakay from "../Layout/imgs/md3.jpg";
import "./ProductCompareEditor.css";

const MAX_COMPARE = 7;

const productThumb = (product) =>
    toImageUrl(getProductPrimaryImage(product)) || imgProductLakay;

const conditionLabel = (condition) =>
    condition === "Used"
        ? elakayAutoText("h_02c0e4a1da6408", "Used")
        : elakayAutoText("h_6403f2b7eb2aaa", "New");

// The saved list as the editor keeps it. A reference the API returned as a
// bare id stays in the list (saving must not drop it) and is shown without
// details.
const toSelection = (entries) =>
    (Array.isArray(entries) ? entries : [])
        .map((entry) => (typeof entry === "string" ? { _id: entry } : entry))
        .filter((entry) => entry && typeof entry === "object" && entry._id);

const selectionKey = (products) => products.map((p) => String(p._id)).join(",");

const ProductDetails = ({ product }) => {
    const price = Number(product?.price);
    // No price in the payload: show nothing rather than "$0.00".
    if (!Number.isFinite(price) || price <= 0) return null;
    const hasDiscount = Number(product?.salePrice) > 0;
    const finalPrice = hasDiscount
        ? (price * (100 - Number(product.salePrice)) / 100).toFixed(2)
        : price.toFixed(2);

    return (
        <span className="prod-compare-details">
            <span className="prod-compare-price">${finalPrice}</span>
            {hasDiscount && (
                <>
                    <span className="prod-compare-price-old">${price.toFixed(2)}</span>
                    <span className="prod-compare-discount">-{product.salePrice}%</span>
                </>
            )}
            {product?.condition && (
                <span className="prod-compare-condition">{conditionLabel(product.condition)}</span>
            )}
        </span>
    );
};

const ProductCompareEditor = ({ token, productID, currentCompare = [], requiresDropshippingPassword = false, onSaved }) => {
    const [selected, setSelected] = useState(() => toSelection(currentCompare));
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const debounceRef = useRef(null);
    const searchSeq = useRef(0);

    // The saved list reaches this editor after the product is loaded (the tab
    // can be opened first, e.g. ?tab=compare) and again after a save: follow it
    // whenever the saved ids change. Not on every parent render, which would
    // wipe a selection that is not saved yet.
    const savedKey = `${productID}:${selectionKey(toSelection(currentCompare))}`;
    useEffect(() => {
        setSelected(toSelection(currentCompare));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedKey]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // Any answer still on its way belongs to an older search term.
        const seq = ++searchSeq.current;
        if (!searchTerm.trim()) {
            setResults([]);
            setSearching(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            const data = await list(0, 8, searchTerm.trim());
            if (seq !== searchSeq.current) return;
            setResults(Array.isArray(data?.data) ? data.data : []);
            setSearching(false);
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm]);

    const isSelected = (id) => selected.some((p) => String(p._id) === String(id));

    const addProduct = (product) => {
        if (String(product._id) === String(productID)) return;
        if (isSelected(product._id) || selected.length >= MAX_COMPARE) return;
        setSelected((prev) => [...prev, product]);
        setSearchTerm("");
        setResults([]);
    };

    const removeProduct = (id) => {
        setSelected((prev) => prev.filter((p) => String(p._id) !== String(id)));
    };

    const save = async () => {
        setSaving(true);
        setSaveError("");
        try {
            const result = await updateProduct(token, productID, {
                ProdCompare: selected.map((p) => ({ _id: p._id })),
                // Same marker as the product form: editing a Dropshipping Out
                // product goes through the Elakay password confirmation. Without
                // it the server refused the save and nothing told the Seller.
                ...(requiresDropshippingPassword ? { dropshippingPasswordRequired: true } : {}),
            });
            if (!result || result.success === false || result.error) {
                setSaveError(elakayAutoText("h_cmp_save_failed", "The comparisons could not be saved. Please try again."));
                return;
            }
            onSaved && onSaved();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="prod-compare-editor">
            <p className="prod-compare-hint">
                {elakayAutoText("h_413a18cf368de6", "Search for other products and add up to 7 to show as comparisons on this product's page.")}
            </p>

            <div className="prod-compare-search">
                <MdSearch className="prod-compare-search-icon" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={elakayAutoText("h_10594f22d1ffe0", "Search products by name...")}
                    disabled={selected.length >= MAX_COMPARE}
                />
            </div>

            {searching && (
                <div className="prod-compare-searching">
                    {elakayAutoText("h_ba2b5a5fc869cc", "Searching...")}
                </div>
            )}

            {results.length > 0 && (
                <div className="prod-compare-results">
                    {results.map((product) => (
                        <button
                            type="button"
                            key={product._id}
                            className="prod-compare-result"
                            onClick={() => addProduct(product)}
                            disabled={isSelected(product._id) || String(product._id) === String(productID)}
                        >
                            <img src={productThumb(product)} alt={product.name} />
                            <span className="prod-compare-result-info">
                                <span className="prod-compare-result-name">{product.name}</span>
                                <ProductDetails product={product} />
                            </span>
                        </button>
                    ))}
                </div>
            )}

            <div className="prod-compare-selected">
                {selected.length === 0 && (
                    <p className="prod-compare-empty">
                        {elakayAutoText("h_33b4567d874620", "No comparison products selected yet.")}
                    </p>
                )}
                {selected.map((product) => (
                    <div className="prod-compare-chip" key={product._id}>
                        <img src={productThumb(product)} alt={product.name || ""} />
                        <span className="prod-compare-chip-info">
                            <span className="prod-compare-chip-name">
                                {product.name || elakayAutoText("h_cmp_unavailable", "Product unavailable")}
                            </span>
                            <ProductDetails product={product} />
                        </span>
                        <button
                            type="button"
                            onClick={() => removeProduct(product._id)}
                            aria-label={elakayAutoText("h_e963907dac5cd5", "Remove")}
                        >
                            <MdClose />
                        </button>
                    </div>
                ))}
            </div>

            {saveError && (
                <p className="prod-compare-error" role="alert">{saveError}</p>
            )}

            <div className="prod-compare-footer">
                <span className="prod-compare-count">{selected.length}/{MAX_COMPARE}</span>
                <button
                    type="button"
                    className="prod-compare-save-btn"
                    onClick={save}
                    disabled={saving}
                >
                    {saving
                        ? elakayAutoText("h_ae7e887517b0f2", "Saving...")
                        : elakayAutoText("h_e2da9c4203e97d", "Save comparisons")}
                </button>
            </div>
        </div>
    );
};

export default ProductCompareEditor;
